import { Collab, Campaign, CampaignType } from "@foodfluence/shared";
import { doesConversationExist, createNewConversation } from '../services/firebaseService';
import { toDate, formatPostingDate } from './dateUtils';
import { displayCollabSubmissionReminder } from "features/collabs/utils/dateUtils";
import { Timestamp } from "firebase/firestore";

/** Collab has not yet been accepted by either party (still in negotiation). */
export const isPreAcceptance = (collab: Collab): boolean =>
  collab.status === 'pendingInfluencerApproval' ||
  collab.status === 'influencerCountered' ||
  collab.status === 'pendingClientApproval';

/** Statuses that indicate an influencer has an active collab (exclude from available campaigns). */
export const ACTIVE_COLLAB_STATUSES = [
  'pendingInfluencerApproval',
  'pendingClientApproval',
  'influencerCountered',
  'pendingRedemption',
  'collabRedeemed',
  'pendingShipment',
  'pendingSubmission',
  'collabCompleted'
] as const;

/** Get campaign IDs where the influencer has an active collab. */
export const getCampaignIdsWithActiveCollab = (collabData: Collab[]): Set<string> => {
  const campaignIds = new Set<string>();
  for (const collab of collabData) {
    if (
      (ACTIVE_COLLAB_STATUSES as readonly string[]).includes(collab.status) &&
      collab.influencerCampaignID
    ) {
      campaignIds.add(collab.influencerCampaignID);
    }
  }
  return campaignIds;
};

export const getEffectiveEndDate = (campaign?: Campaign, collab?: Collab): { seconds: number; nanoseconds: number } | null => {
    return collab?.endDate || campaign?.endDate;
  };

export const getClientIdFromCollabRequest = (collab: Collab, campaign: Campaign): string => {
    return collab?.clientID || campaign?.clientID;
}

export const isShippingCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === 'shipToHome' || campaignType === 'shopify';
}

export const isLocationfulCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === 'inStore' || campaignType === 'retail';
}

export const isRetailCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === 'retail';
}

export const isInStoreCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === 'inStore';
}

export const isShopifyCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === 'shopify';
}

export const isShipToHomeCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === 'shipToHome';
}

export const isBrandManagedCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === 'brandManaged';
}

export const isCreditValidCampaignType = (campaignType?: CampaignType): boolean => {
    return campaignType === "inStore";
}

/**
 * Collab is in pendingRedemption and the influencer has already confirmed their schedule
 * (i.e. NOT awaiting confirmation anymore). Relevant for inStore and retail only.
 */
export const isConfirmed = (collab: Collab, campaign: Campaign): boolean => {
  if (!collab || !campaign) return false;
  if (collab.status !== 'pendingRedemption') return false;
  if (campaign?.campaignType === 'inStore') return !!collab.scheduledCollabDate || collab.clientRequestedReschedule;
  if (campaign?.campaignType === 'retail') return !!collab.scheduledPostingDate || collab.clientRequestedReschedule;
  if (isShippingCampaignType(campaign?.campaignType)) return !!collab.shippingAddress;
  return false;
};

/** Checks if a collab is awaiting influencer confirmation (pure predicate, no UI deps). */
const isAwaitingInfluencerConfirmation = (collab: Collab, campaign: Campaign): boolean => {
  if (collab.status !== 'pendingRedemption') return false;
  if (campaign?.campaignType === 'inStore') return !collab.scheduledCollabDate && !collab.clientRequestedReschedule;
  if (isShippingCampaignType(campaign?.campaignType)) return !collab.shippingAddress && !collab.clientRequestedReschedule;
  if (campaign?.campaignType === 'retail') return !collab.scheduledPostingDate && !collab.clientRequestedReschedule;
  return false;
};

/** Checks if an extension request was made within the last week. */
export const hasRecentExtensionRequest = (collab: Collab): boolean => {
  const requestTime = toDate(collab.endDateExtensionRequestTime);
  if (!requestTime) return false;
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return requestTime > oneWeekAgo;
};

/** Checks if a waitlist request was made within the last week. */
export const hasRecentWaitlistRequest = (collab: Collab): boolean => {
  const requestTime = toDate(collab.waitlistRequestTime);
  if (!requestTime) return false;
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return requestTime > oneWeekAgo;
};

/** Checks if a campaign has ended based on its end date and collab status. */
export const isCampaignEnded = (campaign: Campaign, collab: Collab): boolean => {
  let endDate = getEffectiveEndDate(campaign, collab);
  if (!endDate) {
    const derived = getEndDateFromAvailableTimes(collab, campaign);
    if (!derived) return false;
    endDate = { seconds: derived.seconds, nanoseconds: derived.nanoseconds };
  }
  const end = toDate(endDate);
  if (!end || end >= new Date()) return false;
  if (collab.status === "pendingInfluencerApproval") return true;
  if (isAwaitingInfluencerConfirmation(collab, campaign)) return true;
  return false;
};

/** Checks if a collab needs an extension request (past end date, or in-store slots fully booked). */
export const needsExtensionRequest = (
  campaign: Campaign,
  collab: Collab,
  schedulingFullyBooked = false
): boolean => {
  if (
    schedulingFullyBooked &&
    isInStoreCampaignType(campaign?.campaignType) &&
    (collab.status === 'pendingInfluencerApproval' ||
      (collab.status === 'pendingRedemption' &&
        !collab.scheduledCollabDate &&
        !collab.clientRequestedReschedule))
  ) {
    return true;
  }
  return isCampaignEnded(campaign, collab);
};

export const ensureConversationExists = async (collabId: string, clientID?: string): Promise<void> => {
    const conversationExists = await doesConversationExist(collabId);
    if (!conversationExists) {
        await createNewConversation(collabId, clientID);
    }
}

export const calculateRemainingTime = (collab: Collab, campaign: Campaign): string => {

    if (displayCollabSubmissionReminder(collab, campaign)) {
        return null;
    }
    
    // Handle pendingSubmission case
    if (collab.scheduledPostingDate) {
        return "Posting on " + formatPostingDate(collab.scheduledPostingDate);
    }

    // Return empty string for non-relevant states
    if (!['pendingInfluencerApproval', 'pendingRedemption', 'collabRedeemed'].includes(collab.status) || collab.clientRequestedReschedule) {
        return '';
    }

    let startTime: Date | null = null;
    let duration: number = 0;

    // Set start time and duration based on collab status
    if (collab.status === 'pendingInfluencerApproval') {
        startTime = collab.collabCounteredTime ? toDate(collab.collabCounteredTime) : toDate(collab.collabReceivedTime);
        duration = 72 * 60 * 60 * 1000; // 72 hours
    } else if (collab.status === 'pendingRedemption') {
        if (collab.scheduledPostingDate) {
            return formatPostingDate(collab.scheduledPostingDate);
        } else if (!collab.scheduledCollabDate) {
            startTime = toDate(collab.collabAcceptedTime);
            duration = 7 * 24 * 60 * 60 * 1000; // 1 week
        } else {
            return toDate(collab.scheduledCollabDate)?.toLocaleString(undefined, { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit', 
                hour12: true 
            }) || '';
        }
    } else if (collab.status === 'collabRedeemed') {
        startTime = toDate(collab.collabRedeemedTime);
        duration = 24 * 60 * 60 * 1000; // 24 hours
    }

    // Handle invalid date
    if (!startTime || isNaN(startTime.getTime())) {
        return 'Invalid Date';
    }

    const endTime = new Date(startTime.getTime() + duration);
    const timeLeft = endTime.getTime() - Date.now();

    // Handle expired cases
    if (timeLeft <= 0 && (
        collab.status === 'pendingInfluencerApproval' || 
        (collab.status === "pendingRedemption" && !collab.scheduledCollabDate) || 
        collab.status === "collabRedeemed"
    )) {
        if (needsExtensionRequest(campaign, collab)) {
            return '';
        }
        return 'Expiring soon';
    }

    // Calculate remaining time
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

    return days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes}m`;
}

/**
 * Gets the available times from either collab or campaign
 * @param collab The collab object
 * @param campaign The campaign object
 * @returns The available times and whether they are specific intervals
 */
export const getCollabAvailableTimes = (
  collab?: Partial<Collab>,
  campaign?: Campaign | null
): {
  availableTimes: Record<string, { startTime: string; endTime: string; }[]>;
  hasSpecificTimeIntervals: boolean;
} => {
  // First try to get times from collab
  const collabTimes = collab?.availableTimes || {};
  const hasCollabTimes = Object.keys(collabTimes).length > 0;

  // If collab has times, use those
  if (hasCollabTimes) {
    return {
      availableTimes: collabTimes,
      hasSpecificTimeIntervals: true
    };
  }

  // Otherwise fall back to campaign times
  const campaignTimes = campaign?.availableTimes || {};
  const hasCampaignTimes = Object.keys(campaignTimes).length > 0;

  return {
    availableTimes: campaignTimes,
    hasSpecificTimeIntervals: hasCampaignTimes
  };
};

export const isInStoreWithAvailableTimes = (collab: Collab, campaign: Campaign): boolean => {
  if (campaign.campaignType !== 'inStore') return false;
  const { availableTimes } = getCollabAvailableTimes(collab, campaign);
  return Object.keys(availableTimes).length > 0;
};

/**
 * Checks if a shipping location (country/state) is excluded from a campaign
 * @param campaign The campaign to check
 * @param country The country code/name
 * @param state The state/province name
 * @returns True if the location is excluded, false otherwise
 */
/** Latest calendar day covered by collab-specific `availableTimes`, else campaign's. */
export const getEndDateFromAvailableTimes = (
  collab?: Partial<Collab> | null,
  campaign?: Campaign | null
): Timestamp | null => {
  const { availableTimes } = getCollabAvailableTimes(collab ?? undefined, campaign ?? undefined);
  if (!availableTimes || typeof availableTimes !== 'object') return null;
  const dates = Object.keys(availableTimes).sort();
  if (dates.length === 0) return null;
  const parts = dates[dates.length - 1].split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [year, month, day] = parts;
  // 3pm UTC of the next calendar day — covers end-of-day in any US timezone
  const endDate = new Date(Date.UTC(year, month - 1, day + 1, 12, 0, 0, 0));
  return Timestamp.fromDate(endDate);
};